import React, { useState, useCallback } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';

import RichEditorContent from 'components/RichEditorContent';

function CmsElementBlurb(props) {
  const { index, showElementSettings, editMode } = props;

  // --- Local State ---
  const [shown, setShown] = useState(false);

  // --- Abstract Logic Hook ---
  const {
    getMainId,
    getCssClasses,
    getElementStyle,
    getBackgroundStyle,
    getParamValue,
    getParallaxData,
    getCustomId,
    getAppendHtml,
  } = useAbstractCmsElement(props);

  // --- dnd-kit Setup ---
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: index });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 1000 : 1,
  };

  // --- Handlers ---
  const handleShown = useCallback(() => setShown(true), []);

  const handleShowSettings = (e) => {
    if (showElementSettings) {
      showElementSettings(e, index);
    }
  };

  // --- UI Variables ---
  const shownClass = shown ? 'elem-shown ' : 'elem-hidden ';
  const imageClass = shown ? 'image-shown ' : 'image-not-shown-yet ';
  const imagePositionClass = getParamValue('imagePosition', 'top') === 'left' ? 'image-left ' : '';
  const mainColorClass = `text-${getParamValue('mainColor', 'master')} `;
  const parallaxData = getParallaxData();

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-blurb-content ${shownClass}${imagePositionClass}${mainColorClass}${getCssClasses()}`}
      style={{
        ...getBackgroundStyle(),
        /*
           Pointer events are disabled in editMode to let dnd-kit handle dragging.
           Settings are still accessible via the onClick on the wrapper.
        */
        pointerEvents: editMode ? 'none' : 'auto',
        userSelect: editMode ? 'none' : 'auto',
      }}
    >
      {getElementStyle()}

      <div className="ms-blurb-image m-b-30">
        {getParamValue('useImage', false) ? (
          <img src={getParamValue('imageUrl')} alt="" className={`img-responsive ${imageClass}`} />
        ) : (
          <i
            className={`${imageClass} fa-fw ${getParamValue('icon', 'far fa-check-circle')}`}
            style={{ color: getParamValue('iconColor', 'inherit') }}
          />
        )}
      </div>

      <div className="ms-blurb-container">
        <h4 className={`no-margin p-b-5 ${mainColorClass}`}>{getParamValue('title', '')}</h4>
        <RichEditorContent content={`<p>${getParamValue('content', '')}</p>`} />
      </div>

      {getAppendHtml()}
    </div>
  );

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      /* eslint-disable react/jsx-props-no-spreading */
      {...attributes}
      {...listeners}
      /* eslint-enable react/jsx-props-no-spreading */
      onClick={editMode ? handleShowSettings : undefined}
    >
      {/* eslint-disable react/jsx-props-no-spreading */}
      <CustomScrollTrigger className="cms-overflow-fix" onEnter={handleShown} {...getCustomId()}>
        {parallaxData ? <Parallax {...parallaxData}>{innerContent}</Parallax> : innerContent}
      </CustomScrollTrigger>
      {/* eslint-enable react/jsx-props-no-spreading */}

      {/* Visual drag indicator overlay */}
      {editMode && (
        <div
          className="drag-handle-indicator"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            border: isDragging ? '2px solid #007bff' : '1px dashed transparent',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
}

export default CmsElementBlurb;
